import { googleNative, isGoogleNativePlatform } from '@/services/googleAuth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError, apiRequest, getDeviceId } from '@/services/appApi';
import { clearPlatformSession, loadPlatformSession, savePlatformSession } from '@/services/platformSessionStore';

export interface AppUser {
  id: string;
  user_id: string;
  email?: string | null;
  email_masked?: string | null;
  phone_masked?: string | null;
  nickname?: string | null;
  avatar_url?: string | null;
  two_factor_status: 'off' | 'on';
  risk_ack_version?: string | null;
  terms_version_accepted?: string | null;
  user_metadata: {
    full_name?: string | null;
    avatar_url?: string | null;
  };
}

export interface AppSession {
  access_token: string;
  refresh_token: string;
  refresh_jti?: string;
  expires_in?: number;
  user: AppUser;
}

interface RequestOtpResult {
  challenge_id: string;
  expire_seconds: number;
  rate_seconds: number;
  delivery_mode?: 'email' | 'fixed_code';
  debug_otp_code?: string | null;
  requires_terms_acceptance?: boolean;
  terms_version?: string | null;
}

export interface VerifyOtpResult {
  auth_status: 'authenticated' | '2fa_required';
  temp_token?: string;
  access_token?: string;
  refresh_token?: string;
  refresh_jti?: string;
  expires_in?: number;
  user: Omit<AppUser, 'id' | 'email' | 'user_metadata'> & {
    user_id: string;
  };
}

interface TwoFaSetupResult {
  setup_id: string;
  secret_once: string;
  otpauth_uri: string;
  expires_in: number;
}

interface AuthContextType {
  user: AppUser | null;
  session: AppSession | null;
  loading: boolean;
  authError: string | null;
  accessToken: string | null;
  acceptGoogleSession: (data: VerifyOtpResult) => Promise<VerifyOtpResult>;
  acceptExternalSession: (data: VerifyOtpResult) => Promise<VerifyOtpResult>;
  requestLoginOtp: (email: string, invitationCode?: string) => Promise<RequestOtpResult>;
  verifyLoginOtp: (params: { challengeId: string; otp: string; agreedTerms: boolean }) => Promise<VerifyOtpResult>;
  verifyLogin2Fa: (params: { tempToken: string; totpCode: string }) => Promise<VerifyOtpResult>;
  setup2Fa: () => Promise<TwoFaSetupResult>;
  enable2Fa: (setupId: string, totpCode: string) => Promise<{ two_factor_status: 'on'; enabled_at: number }>;
  disable2Fa: (totpCode: string) => Promise<{ two_factor_status: 'off'; disabled_at: number }>;
  refreshCurrentUser: () => Promise<AppUser | null>;
  updateUserProfile: (params: { nickname?: string; avatarUrl?: string | null; riskAckVersion?: string }) => Promise<AppUser>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  verifyOtp: (email: string, otp: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  authError: null,
  accessToken: null,
  acceptGoogleSession: async data => data,
  acceptExternalSession: async data => data,
  requestLoginOtp: async () => ({ challenge_id: '', expire_seconds: 0, rate_seconds: 60 }),
  verifyLoginOtp: async () => ({ auth_status: 'authenticated', user: { user_id: '', two_factor_status: 'off' } }),
  verifyLogin2Fa: async () => ({ auth_status: 'authenticated', user: { user_id: '', two_factor_status: 'off' } }),
  setup2Fa: async () => ({ setup_id: '', secret_once: '', otpauth_uri: '', expires_in: 0 }),
  enable2Fa: async () => ({ two_factor_status: 'on', enabled_at: 0 }),
  disable2Fa: async () => ({ two_factor_status: 'off', disabled_at: 0 }),
  refreshCurrentUser: async () => null,
  updateUserProfile: async () => normalizeUser({ user_id: '', two_factor_status: 'off' }),
  signIn: async () => ({ error: '请使用验证码登录' }),
  signUp: async () => ({ error: '请使用验证码登录' }),
  signOut: async () => {},
  verifyOtp: async () => ({ error: '请使用验证码登录' }),
});

function normalizeUser(raw: VerifyOtpResult['user'] | AppUser): AppUser {
  const userId = raw.user_id || (raw as AppUser).id;
  const displayName = raw.nickname || raw.email_masked || raw.phone_masked || 'ForexMind User';
  return {
    ...raw,
    id: userId,
    user_id: userId,
    email: raw.email_masked ?? null,
    two_factor_status: raw.two_factor_status || 'off',
    user_metadata: {
      full_name: displayName,
      avatar_url: raw.avatar_url ?? null,
    },
  };
}

function sessionFromResult(data: VerifyOtpResult): AppSession {
  if (!data.access_token || !data.refresh_token) {
    throw new Error('登录返回缺少 token');
  }
  const user = normalizeUser(data.user);
  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    refresh_jti: data.refresh_jti,
    expires_in: data.expires_in,
    user,
  };
}

let refreshInFlight: { token: string; promise: Promise<VerifyOtpResult> } | null = null;

function refreshStoredSession(refreshToken: string): Promise<VerifyOtpResult> {
  if (refreshInFlight?.token === refreshToken) return refreshInFlight.promise;
  const promise = apiRequest<VerifyOtpResult>('/app/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
  }).finally(() => {
    if (refreshInFlight?.promise === promise) refreshInFlight = null;
  });
  refreshInFlight = { token: refreshToken, promise };
  return promise;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AppSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let stored: AppSession | null = null;
      try {
        stored = await loadPlatformSession();
        if (!stored?.refresh_token || cancelled) return;
        const data = await refreshStoredSession(stored.refresh_token);
        if (cancelled) return;
        const next = sessionFromResult(data);
        await savePlatformSession(next);
        if (cancelled) return;
        setSession(next);
        setAuthError(null);
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          try { await clearPlatformSession(); } catch { /* Login remains closed. */ }
          setSession(null);
          setAuthError('登录已失效，请重新登录');
        } else {
          setSession(stored);
          setAuthError('登录状态暂时无法同步，请检查网络后重试');
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const applyAuthenticatedResult = useCallback(async (data: VerifyOtpResult) => {
    const next = sessionFromResult(data);
    await savePlatformSession(next);
    setSession(next);
    return data;
  }, []);

  const requestLoginOtp = useCallback(async (email: string, invitationCode?: string) => {
    return apiRequest<RequestOtpResult>('/app/auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim(),
        purpose: 'login',
        device_id: getDeviceId(),
        invitation_code: invitationCode?.trim() || undefined,
      }),
    });
  }, []);

  const verifyLoginOtp = useCallback(async (params: { challengeId: string; otp: string; agreedTerms: boolean }) => {
    const data = await apiRequest<VerifyOtpResult>('/app/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({
        challenge_id: params.challengeId,
        otp: params.otp,
        device_id: getDeviceId(),
        agreed_terms: params.agreedTerms,
      }),
    });
    if (data.auth_status === 'authenticated') await applyAuthenticatedResult(data);
    return data;
  }, [applyAuthenticatedResult]);

  const verifyLogin2Fa = useCallback(async (params: { tempToken: string; totpCode: string }) => {
    const data = await apiRequest<VerifyOtpResult>('/app/auth/verify-2fa', {
      method: 'POST',
      body: JSON.stringify({ totp_code: params.totpCode }),
    }, params.tempToken);
    return applyAuthenticatedResult(data);
  }, [applyAuthenticatedResult]);

  const setup2Fa = useCallback(async () => {
    return apiRequest<TwoFaSetupResult>('/app/security/2fa/setup', { method: 'POST' }, session?.access_token);
  }, [session]);

  const enable2Fa = useCallback(async (setupId: string, totpCode: string) => {
    const result = await apiRequest<{ two_factor_status: 'on'; enabled_at: number }>('/app/security/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ setup_id: setupId, totp_code: totpCode }),
    }, session?.access_token);
    if (session) {
      const next = { ...session, user: normalizeUser({ ...session.user, two_factor_status: 'on' }) };
      await savePlatformSession(next);
      setSession(next);
    }
    return result;
  }, [session]);

  const disable2Fa = useCallback(async (totpCode: string) => {
    const result = await apiRequest<{ two_factor_status: 'off'; disabled_at: number }>('/app/security/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ totp_code: totpCode }),
    }, session?.access_token);
    if (session) {
      const next = { ...session, user: normalizeUser({ ...session.user, two_factor_status: 'off' }) };
      await savePlatformSession(next);
      setSession(next);
    }
    return result;
  }, [session]);

  const refreshCurrentUser = useCallback(async () => {
    if (!session?.access_token) return null;
    const rawUser = await apiRequest<AppUser>('/app/users/me', { method: 'GET' }, session.access_token);
    const user = normalizeUser(rawUser);
    const next = { ...session, user };
    await savePlatformSession(next);
    setSession(next);
    return user;
  }, [session]);

  const updateUserProfile = useCallback(async (params: { nickname?: string; avatarUrl?: string | null; riskAckVersion?: string }) => {
    if (!session?.access_token) throw new Error('请先登录');

    let nextUser = session.user;
    if (params.nickname !== undefined || params.riskAckVersion !== undefined) {
      nextUser = normalizeUser(await apiRequest<AppUser>('/app/users/me', {
        method: 'PATCH',
        body: JSON.stringify({
          ...(params.nickname !== undefined ? { nickname: params.nickname } : {}),
          ...(params.riskAckVersion !== undefined ? { risk_ack_version: params.riskAckVersion } : {}),
        }),
      }, session.access_token));
    }

    if (params.avatarUrl && params.avatarUrl !== nextUser.avatar_url) {
      const result = await apiRequest<{ avatar_url: string; user: AppUser }>('/app/users/avatar', {
        method: 'POST',
        body: JSON.stringify({ avatar_url: params.avatarUrl }),
      }, session.access_token);
      nextUser = normalizeUser(result.user);
    }

    const next = { ...session, user: nextUser };
    await savePlatformSession(next);
    setSession(next);
    return nextUser;
  }, [session]);

  const signOut = useCallback(async () => {
    const refreshJti = session?.refresh_jti;
    try {
      await apiRequest('/app/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refresh_jti: refreshJti }),
      }, session?.access_token);
    } catch {
      // Local logout must still clear client state if the network is unavailable.
    }
    if (isGoogleNativePlatform()) {
      try { await googleNative.signOut(); } catch { /* Always clear the platform session. */ }
    }
    await clearPlatformSession();
    setSession(null);
  }, [session?.access_token, session?.refresh_jti]);

  const legacyPasswordFlow = useCallback(async () => ({ error: '请使用邮箱验证码登录' }), []);
  const legacyVerifyOtp = useCallback(async () => ({ error: '请使用登录页验证码流程' }), []);

  const value = useMemo<AuthContextType>(() => ({
    user: session?.user ?? null,
    session,
    loading,
    authError,
    accessToken: session?.access_token ?? null,
    acceptGoogleSession: applyAuthenticatedResult,
    acceptExternalSession: applyAuthenticatedResult,
    requestLoginOtp,
    verifyLoginOtp,
    verifyLogin2Fa,
    setup2Fa,
    enable2Fa,
    disable2Fa,
    refreshCurrentUser,
    updateUserProfile,
    signIn: legacyPasswordFlow,
    signUp: legacyPasswordFlow,
    signOut,
    verifyOtp: legacyVerifyOtp,
  }), [
    session,
    loading,
    authError,
    applyAuthenticatedResult,
    requestLoginOtp,
    verifyLoginOtp,
    verifyLogin2Fa,
    setup2Fa,
    enable2Fa,
    disable2Fa,
    refreshCurrentUser,
    updateUserProfile,
    legacyPasswordFlow,
    signOut,
    legacyVerifyOtp,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
